import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { localStorageSetItemWithQuotaRecovery } from './localStorageQuota';
import {
  appendPresentationRefOwnerFromLearningText,
  isPersistableUuid,
  resolvePresentationRefIdFromLearningText,
} from './programContentSlideOwner';

/** 수업 프레젠 저장 후 메인 프레젠·대시보드가 REST 없이 같은 칸을 다시 열 때 쓰는 학습내용 오버레이 */
export const PROGRAM_CONTENT_SLIDE_LEARNING_OVERLAY_PREFIX =
  'cp:program-content-slide-learning-overlay:v1:';

const MAX_OVERLAY_ENTRIES = 200;

export type ProgramContentSlideLearningOverlayEntry = {
  learningText: string;
  presentationRefId?: string;
  owner?: ProgramContentSlideOwnerRef;
  updatedAt: number;
};

type OverlayStoreV1 = {
  v: 1;
  byAnnualKey: Record<string, ProgramContentSlideLearningOverlayEntry>;
  byOwnerKey: Record<string, ProgramContentSlideLearningOverlayEntry>;
};

export type ProgramContentSlideBindingSlotFields = {
  annualCellKey: string;
  slideOwner: ProgramContentSlideOwnerRef | null;
  slideOwnerCandidates: ProgramContentSlideOwnerRef[];
  slideOpenDbOwner: ProgramContentSlideOwnerRef | null;
  slideLearningTextRaw: string;
  slideCreatePresentationRefOnSave?: { learningText?: string } | null;
};

function overlayStorageKey(workspaceId: string): string {
  return PROGRAM_CONTENT_SLIDE_LEARNING_OVERLAY_PREFIX + workspaceId.trim();
}

function ownerLookupKey(owner: ProgramContentSlideOwnerRef): string {
  return `${owner.kind}:${owner.id.trim()}`;
}

function emptyStore(): OverlayStoreV1 {
  return { v: 1, byAnnualKey: {}, byOwnerKey: {} };
}

function parseStore(raw: string | null): OverlayStoreV1 {
  if (!raw) return emptyStore();
  try {
    const parsed = JSON.parse(raw) as OverlayStoreV1;
    if (!parsed || parsed.v !== 1 || typeof parsed !== 'object') return emptyStore();
    return {
      v: 1,
      byAnnualKey: parsed.byAnnualKey && typeof parsed.byAnnualKey === 'object' ? parsed.byAnnualKey : {},
      byOwnerKey: parsed.byOwnerKey && typeof parsed.byOwnerKey === 'object' ? parsed.byOwnerKey : {},
    };
  } catch {
    return emptyStore();
  }
}

function readStore(workspaceId: string): OverlayStoreV1 {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return emptyStore();
  try {
    return parseStore(window.localStorage.getItem(overlayStorageKey(sid)));
  } catch {
    return emptyStore();
  }
}

function pruneOldest(
  map: Record<string, ProgramContentSlideLearningOverlayEntry>,
  max: number
): Record<string, ProgramContentSlideLearningOverlayEntry> {
  const keys = Object.keys(map);
  if (keys.length <= max) return map;
  const sorted = keys.sort((a, b) => (map[a]?.updatedAt ?? 0) - (map[b]?.updatedAt ?? 0));
  const drop = sorted.slice(0, keys.length - max);
  if (!drop.length) return map;
  const next = { ...map };
  drop.forEach((k) => {
    delete next[k];
  });
  return next;
}

function persistStore(workspaceId: string, store: OverlayStoreV1): void {
  const sid = workspaceId.trim();
  if (!sid || typeof window === 'undefined') return;
  const next: OverlayStoreV1 = {
    v: 1,
    byAnnualKey: pruneOldest(store.byAnnualKey, MAX_OVERLAY_ENTRIES),
    byOwnerKey: pruneOldest(store.byOwnerKey, MAX_OVERLAY_ENTRIES),
  };
  try {
    localStorageSetItemWithQuotaRecovery(overlayStorageKey(sid), JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function writeProgramContentSlideLearningOverlay(params: {
  workspaceId: string;
  annualKey?: string;
  learningText: string;
  presentationRefId?: string;
  owner?: ProgramContentSlideOwnerRef | null;
}): void {
  const sid = params.workspaceId.trim();
  if (!sid) return;
  const annualKey = (params.annualKey ?? '').trim();
  const owner = params.owner?.id?.trim()
    ? ({ kind: params.owner.kind, id: params.owner.id.trim() } as ProgramContentSlideOwnerRef)
    : undefined;
  const fromText = resolvePresentationRefIdFromLearningText(params.learningText);
  const presentationRefId =
    (params.presentationRefId ?? '').trim() || fromText || undefined;
  const ref =
    presentationRefId && isPersistableUuid(presentationRefId) ? presentationRefId : undefined;
  if (!annualKey && !owner && !ref) return;

  const entry: ProgramContentSlideLearningOverlayEntry = {
    learningText: params.learningText,
    updatedAt: Date.now(),
    ...(ref ? { presentationRefId: ref } : {}),
    ...(owner ? { owner } : {}),
  };
  const store = readStore(sid);
  if (annualKey) store.byAnnualKey[annualKey] = entry;
  if (owner && owner.kind !== 'presentation_ref') {
    store.byOwnerKey[ownerLookupKey(owner)] = entry;
  }
  if (ref) {
    store.byOwnerKey[ownerLookupKey({ kind: 'presentation_ref', id: ref })] = entry;
  }
  persistStore(sid, store);
}

function lookupOverlayInStore(
  store: OverlayStoreV1,
  slot: ProgramContentSlideBindingSlotFields
): ProgramContentSlideLearningOverlayEntry | null {
  const annualKey = (slot.annualCellKey ?? '').trim();
  if (annualKey && store.byAnnualKey[annualKey]) return store.byAnnualKey[annualKey];

  const owners: ProgramContentSlideOwnerRef[] = [];
  if (slot.slideOpenDbOwner) owners.push(slot.slideOpenDbOwner);
  if (slot.slideOwner) owners.push(slot.slideOwner);
  for (const c of slot.slideOwnerCandidates ?? []) owners.push(c);
  for (const o of owners) {
    const id = o?.id?.trim();
    if (!id) continue;
    const hit = store.byOwnerKey[ownerLookupKey({ kind: o.kind, id })];
    if (hit) return hit;
  }
  return null;
}

function lookupOverlayForSlot(
  workspaceId: string,
  slot: ProgramContentSlideBindingSlotFields
): ProgramContentSlideLearningOverlayEntry | null {
  return lookupOverlayInStore(readStore(workspaceId), slot);
}

/**
 * 저장된 presentation_ref가 있으면 그걸 연다.
 * 메인 프레젠은 presence 캐시 미스로 row owner(빈 캔버스)로 떨어지는 것을 막는다.
 */
export function resolveDashboardSlotPresentationOpenFallbackOwner(
  slot: ProgramContentSlideBindingSlotFields
): ProgramContentSlideOwnerRef | null {
  const owner = slot.slideOwner;
  if (owner?.kind === 'presentation_ref' && isPersistableUuid(owner.id)) return owner;
  return slot.slideOpenDbOwner ?? slot.slideOwner;
}

export function mergeDashboardSlotWithSlideLearningOverlay<T extends ProgramContentSlideBindingSlotFields>(
  slot: T,
  overlay: ProgramContentSlideLearningOverlayEntry
): T {
  const learningText = overlay.learningText;
  const refId =
    (overlay.presentationRefId ?? '').trim() || resolvePresentationRefIdFromLearningText(learningText);
  const ref = refId && isPersistableUuid(refId) ? refId : '';
  if (ref) {
    const presentationOwner: ProgramContentSlideOwnerRef = { kind: 'presentation_ref', id: ref };
    const candidates = appendPresentationRefOwnerFromLearningText(
      slot.slideOwnerCandidates ?? [],
      learningText
    );
    return {
      ...slot,
      slideLearningTextRaw: learningText,
      slideOwner: presentationOwner,
      slideOwnerCandidates: candidates,
      slideCreatePresentationRefOnSave: undefined,
    };
  }

  const rowOwner =
    slot.slideOpenDbOwner?.kind !== 'presentation_ref' && slot.slideOpenDbOwner?.id?.trim()
      ? slot.slideOpenDbOwner
      : slot.slideOwner?.kind !== 'presentation_ref'
        ? slot.slideOwner
        : null;
  const candidates = (slot.slideOwnerCandidates ?? []).filter((o) => o.kind !== 'presentation_ref');
  const createOnSave = slot.slideCreatePresentationRefOnSave
    ? { ...slot.slideCreatePresentationRefOnSave, learningText }
    : slot.slideCreatePresentationRefOnSave;
  return {
    ...slot,
    slideLearningTextRaw: learningText,
    slideOwner: rowOwner,
    slideOwnerCandidates: candidates,
    slideCreatePresentationRefOnSave: createOnSave,
  };
}

export function applyProgramContentSlideLearningOverlayToSlot<T extends ProgramContentSlideBindingSlotFields>(
  slot: T,
  workspaceId: string
): T {
  const overlay = lookupOverlayForSlot(workspaceId, slot);
  if (!overlay) return slot;
  return mergeDashboardSlotWithSlideLearningOverlay(slot, overlay);
}

export function applyProgramContentSlideLearningOverlayToSlots<T extends ProgramContentSlideBindingSlotFields>(
  slots: T[],
  workspaceId: string
): T[] {
  const sid = workspaceId.trim();
  if (!sid || !slots.length) return slots;
  const store = readStore(sid);
  if (!Object.keys(store.byAnnualKey).length && !Object.keys(store.byOwnerKey).length) return slots;
  return slots.map((slot) => {
    const overlay = lookupOverlayInStore(store, slot);
    if (!overlay) return slot;
    return mergeDashboardSlotWithSlideLearningOverlay(slot, overlay);
  });
}
